import { useTheme } from "@/theme/ThemeProvider";
import { batchRowHoldSlack, nextBatchAnimalRow } from "@/ui/intake/batchAnimalLayout";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Platform, type LayoutChangeEvent, type View } from "react-native";

/**
 * Zeile ab der gemessenen Containerbreite.
 * RN-Webs onLayout misst über UIManager.measure erst im setTimeout. Fällt das
 * aus oder meldet es 0, bleibt der Startwert „Stapel“, obwohl die Box breit ist.
 * Deshalb liest Web die Box selbst: einmal beim Einsetzen und danach bei
 * Größenänderung. onLayout bleibt für Native und als zweite Meldung.
 * Bis zur ersten Messung gilt Stapel, nicht die Fensterbreite. Sonst zeigt
 * ein Native-Tablet ab 768 pt einen Frame lang die Zeile. Geht der Host weg
 * (Schritt 1, Drehung dazwischen), wird der gemessene Modus vergessen.
 */
export function useBatchAnimalRow(): {
  row: boolean;
  onLayout: (event: LayoutChangeEvent) => void;
  ref: (node: View | null) => void;
} {
  const { theme } = useTheme();
  const slack = batchRowHoldSlack(theme.space);
  const hostRef = useRef<View | null>(null);
  const knownRef = useRef(false);
  const rowRef = useRef(false);
  const [hostEpoch, setHostEpoch] = useState(0);
  const [known, setKnown] = useState(false);
  const [row, setRow] = useState(false);

  const apply = useCallback(
    (width: number) => {
      if (!(width > 0)) return;
      if (!knownRef.current) {
        knownRef.current = true;
        setKnown(true);
      }
      const next = nextBatchAnimalRow(width, rowRef.current, slack);
      if (next === rowRef.current) return;
      rowRef.current = next;
      setRow(next);
    },
    [slack],
  );

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      apply(event.nativeEvent.layout.width);
    },
    [apply],
  );

  const ref = useCallback((node: View | null) => {
    if (hostRef.current === node) return;
    hostRef.current = node;
    if (node == null) {
      knownRef.current = false;
      rowRef.current = false;
      setKnown(false);
      setRow(false);
    }
    setHostEpoch((n) => n + 1);
  }, []);

  useLayoutEffect(() => {
    if (Platform.OS !== "web") return;
    const node = asElement(hostRef.current);
    if (node == null) return;
    apply(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const current = asElement(hostRef.current);
      if (current == null) return;
      apply(current.getBoundingClientRect().width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [apply, hostEpoch]);

  return {
    row: known ? row : false,
    onLayout,
    ref,
  };
}

function asElement(node: View | null): Element | null {
  if (typeof Element === "undefined" || node == null) return null;
  return (node as unknown) instanceof Element ? (node as unknown as Element) : null;
}
